﻿// src/modules/upload.js — File upload, multipart upload, processing, retry
import { state } from '../state.js';
import { API_BASE, APP_VERSION } from '../utils/constants.js';
import { apiFetch } from '../utils/api.js';
import { showView, showConfirmDialog, showToast, setStatus } from '../utils/dom.js';
import { logger, apiFetchWithRefresh, redactForLogs, addUploadProgressLog, getRecentUploadProgressLogs,
    addUploadDiagnostic, getRecentUploadDiagnostics, preUploadDiagnostic, createUploadResultTelemetry,
} from './debug.js';
import { SessionStore } from '../utils/storage.js';
import { loadRecordingHistory, requestWakeLock, releaseWakeLock, getRecordPatientId, resetRecordPatientPicker } from './recording.js';
import { getAllLoadedNotes, loadNotes } from './notes.js';
import { getSelectedAppointmentType } from './analytics.js';
import { attachPatientPicker, historyLabelSuggestions } from './patients.js';

// B1: unified Patient picker on Upload — #uploadPatientLabel is both the label
// carrier and the picker search field (same treatment as Record).
let uploadPatientPicker = null;

export function initUploadPatientPicker() {
    const host = document.getElementById('uploadPatientPicker');
    if (!host || uploadPatientPicker) return;
    const labelInput = document.getElementById('uploadPatientLabel');
    uploadPatientPicker = attachPatientPicker(host, {
        searchInput: labelInput,
        extraSuggestions: (query, rosterLabels) =>
            historyLabelSuggestions(getAllLoadedNotes(), query, rosterLabels),
    });
    if (!getAllLoadedNotes().length) loadNotes().catch(() => {});
}

/** The selected patientId for an upload, or null when nothing is selected. */
export function getUploadPatientId() {
    return uploadPatientPicker ? uploadPatientPicker.getPatientId() : null;
}

/** Clear the upload picker selection (full state reset after completion / view change). */
export function resetUploadPatientPicker() {
    if (uploadPatientPicker) uploadPatientPicker.reset();
}

// Progress UI helpers (simple DOM toggles, previously in main.js scope)
function showProgress(pEl, fEl, pct) {
    document.getElementById(pEl).classList.add('active');
    document.getElementById(fEl).style.width = pct + '%';
}
function hideProgress(pEl) { document.getElementById(pEl).classList.remove('active'); }

function showInputError(inputEl, errorElId) {
    inputEl.classList.add('input-error');
    const errorEl = document.getElementById(errorElId);
    if (errorEl) errorEl.classList.remove('hidden');
    inputEl.focus();
    const handler = () => {
        inputEl.classList.remove('input-error');
        if (errorEl) errorEl.classList.add('hidden');
        inputEl.removeEventListener('input', handler);
    };
    inputEl.addEventListener('input', handler);
}
export function handleUploadAreaClick() {
    const patientInput = document.getElementById('uploadPatientLabel');
    if (!patientInput.value.trim()) {
        showInputError(patientInput, 'uploadPatientError');
        return;
    }
    // FEATURE 2: Check for duplicate patient tag before upload
    const enteredTag = patientInput.value.trim();
    if (checkDuplicateTag(enteredTag)) {
        confirmDuplicateTag(enteredTag).then((proceed) => {
            if (!proceed) {
                patientInput.focus();
                return;
            }
            // Show custom file select modal
            document.getElementById('fileSelectModal').classList.add('active');
        });
        return;
    }
    // Show custom file select modal
    document.getElementById('fileSelectModal').classList.add('active');
}

export function closeFileSelectModal() {
    document.getElementById('fileSelectModal').classList.remove('active');
}

export function triggerFileInput() {
    closeFileSelectModal();
    document.getElementById('fileInput').click();
}

export function handleFileSelect(event) {
    const file = event.target.files[0];
    if (!file) return;
    setStatus('uploadStatus', 'Selected: ' + file.name + ' (' + (file.size / 1024 / 1024).toFixed(1) + ' MB)', '');
    setTimeout(() => uploadAndProcess(file, 'upload'), 500);
}

// ============================================================================
// UPLOAD & PROCESS (with session-based safety + auto token refresh on 401)
// ============================================================================
let currentIndexedDBId = null; // Legacy â€” kept for compatibility

// ============================================================================
// BACKGROUND TRACKER â€” Safari Background Tab Awareness for Uploads
// ============================================================================
const BackgroundTracker = (() => {
    let _isBackground = false;
    let _backgroundAt = null; // timestamp when tab went to background
    let _totalBackgroundMs = 0; // accumulated background time for current upload session
    let _foregroundCallbacks = []; // callbacks to fire when returning to foreground
    let _uploadActive = false; // true when an upload is in progress

    function init() {
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                _isBackground = true;
                _backgroundAt = Date.now();
                logger.info('BG_TRACKER', 'Tab went to background', { uploadActive: _uploadActive });
            } else {
                const wasBackground = _isBackground;
                const bgDuration = _backgroundAt ? (Date.now() - _backgroundAt) : 0;
                _isBackground = false;
                if (wasBackground && bgDuration > 0) {
                    _totalBackgroundMs += bgDuration;
                    logger.info('BG_TRACKER', 'Tab returned to foreground', {
                        bgDurationMs: bgDuration,
                        totalBgMs: _totalBackgroundMs,
                        uploadActive: _uploadActive,
                    });
                }
                _backgroundAt = null;
                // Fire foreground callbacks (e.g., abort stale XHRs)
                if (wasBackground && _uploadActive) {
                    const callbacks = [..._foregroundCallbacks];
                    callbacks.forEach(cb => { try { cb(bgDuration); } catch(e) {} });
                }
            }
        });
    }

    /** Call when upload starts to begin tracking */
    function startUploadSession() {
        _uploadActive = true;
        _totalBackgroundMs = 0;
        showUploadWarningBanner(true);
    }

    /** Call when upload ends (success or final failure) */
    function endUploadSession() {
        _uploadActive = false;
        _totalBackgroundMs = 0;
        _foregroundCallbacks = [];
        showUploadWarningBanner(false);
    }

    /** Register a callback that fires when tab returns to foreground during upload */
    function onForeground(cb) {
        _foregroundCallbacks.push(cb);
    }

    /** Remove a foreground callback */
    function offForeground(cb) {
        const idx = _foregroundCallbacks.indexOf(cb);
        if (idx >= 0) _foregroundCallbacks.splice(idx, 1);
    }

    /** Check if tab is currently in background */
    function isBackground() { return _isBackground; }

    /** Get how long the tab has been in background (0 if foreground) */
    function currentBackgroundDuration() {
        if (!_isBackground || !_backgroundAt) return 0;
        return Date.now() - _backgroundAt;
    }

    /** Get total accumulated background time during this upload session */
    function totalBackgroundMs() { return _totalBackgroundMs; }

    /** Check if upload is active */
    function isUploadActive() { return _uploadActive; }

    return {
        init, startUploadSession, endUploadSession,
        onForeground, offForeground,
        isBackground, currentBackgroundDuration, totalBackgroundMs, isUploadActive,
    };
})();

// Initialize background tracker immediately
BackgroundTracker.init();

/**
 * Show/hide the "keep tab active" warning banner during uploads.
 */
export function showUploadWarningBanner(show) {
    let banner = document.getElementById('uploadBgWarning');
    if (show) {
        if (!banner) {
            banner = document.createElement('div');
            banner.id = 'uploadBgWarning';
            banner.className = 'upload-bg-warning';
            banner.innerHTML = '<span class="upload-bg-warning-icon">âš \uFE0F</span> Please keep this tab active for fastest upload. Switching tabs may slow or pause the upload.';
            // Insert at top of the active page content area
            const recordTab = document.getElementById('page-record');
            if (recordTab) {
                recordTab.insertBefore(banner, recordTab.firstChild);
            } else {
                document.body.appendChild(banner);
            }
        }
        banner.classList.add('visible');
    } else {
        if (banner) banner.classList.remove('visible');
    }
}

// Chunked upload constants (multipart fallback only)
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB per chunk (S3 multipart minimum)
const MAX_CHUNK_RETRIES = 3;
const MULTIPART_THRESHOLD = 5 * 1024 * 1024; // Files > 5MB use multipart as primary upload method
// v2.9.7: Relaxed background thresholds for multipart fallback
const BG_ABORT_THRESHOLD_MS = 120000; // Only abort if backgrounded > 120 seconds
const BG_FOREGROUND_GRACE_MS = 15000; // Give 15s of foreground time before aborting after bg return

/**
 * Upload a single chunk using XMLHttpRequest (Safari-compatible, has progress events).
 * Background-aware: aborts stale XHR on foreground return and rejects with a
 * special error type so the retry loop can distinguish it from real failures.
 * Returns the ETag from the response headers on success.
 */
export function uploadChunkXHR(url, chunkBlob, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url, true);

        const FOREGROUND_TIMEOUT = 300000; // v2.9.7: 300 seconds per chunk (relaxed)
        let foregroundElapsed = 0;
        let lastTickTime = Date.now();
        let settled = false;

        // Tick-based foreground timer: only counts time when tab is visible
        const chunkTimer = setInterval(() => {
            if (!BackgroundTracker.isBackground()) {
                const now = Date.now();
                foregroundElapsed += (now - lastTickTime);
                lastTickTime = now;
                if (foregroundElapsed >= FOREGROUND_TIMEOUT) {
                    clearInterval(chunkTimer);
                    BackgroundTracker.offForeground(onForegroundReturn);
                    if (!settled) {
                        settled = true;
                        xhr.abort();
                        reject(new Error('Chunk upload timed out (foreground timeout)'));
                    }
                }
            } else {
                // While in background, don't advance the timer â€” just update lastTickTime
                lastTickTime = Date.now();
            }
        }, 1000);

        // v2.9.7: Only abort if tab was backgrounded > 120 seconds, then give 15s grace
        function onForegroundReturn(bgDurationMs) {
            if (bgDurationMs > BG_ABORT_THRESHOLD_MS && !settled) {
                // Tab was backgrounded for > 120s â€” give 15s grace period to make progress
                logger.info('BG_TRACKER', 'Long background detected, starting 15s grace period', {
                    bgDurationMs,
                    foregroundElapsed,
                });
                setTimeout(() => {
                    // After grace period, check if XHR is still pending (no progress made)
                    if (!settled) {
                        logger.info('BG_TRACKER', 'Grace period expired, aborting stale XHR', { bgDurationMs });
                        settled = true;
                        clearInterval(chunkTimer);
                        BackgroundTracker.offForeground(onForegroundReturn);
                        xhr.abort();
                        const err = new Error('XHR aborted after long background (Safari freeze)');
                        err._backgroundAbort = true;
                        reject(err);
                    }
                }, BG_FOREGROUND_GRACE_MS);
            }
        }
        BackgroundTracker.onForeground(onForegroundReturn);

        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onProgress) {
                onProgress(e.loaded, e.total);
            }
        };

        xhr.onload = () => {
            clearInterval(chunkTimer);
            BackgroundTracker.offForeground(onForegroundReturn);
            if (settled) return;
            settled = true;
            if (xhr.status >= 200 && xhr.status < 300) {
                const etag = xhr.getResponseHeader('ETag');
                resolve(etag);
            } else {
                reject(new Error(`Chunk upload failed (HTTP ${xhr.status})`));
            }
        };

        xhr.onerror = () => {
            clearInterval(chunkTimer);
            BackgroundTracker.offForeground(onForegroundReturn);
            if (settled) return;
            settled = true;
            reject(new Error('Network error during chunk upload'));
        };

        xhr.ontimeout = () => {
            clearInterval(chunkTimer);
            BackgroundTracker.offForeground(onForegroundReturn);
            if (settled) return;
            settled = true;
            reject(new Error('Chunk upload timed out (XHR native timeout)'));
        };
        // Set a generous native timeout as a safety net (foreground timer is the real guard)
        xhr.timeout = 300000; // 5 min absolute max (covers long background periods)

        xhr.send(chunkBlob);
    });
}

/**
 * Multipart chunked upload for large files (>5MB).
 * Uses S3 multipart upload via Lambda endpoints + XMLHttpRequest for Safari compatibility.
 * Returns { s3Key } on success.
 */
export async function uploadMultipart(audioBlob, contentType, patientLabel, profileId, onProgress) {
    const totalSize = audioBlob.size;
    const numParts = Math.ceil(totalSize / CHUNK_SIZE);

    logger.info('UPLOAD', 'Starting multipart upload', { totalSize, numParts, chunkSize: CHUNK_SIZE });
    BackgroundTracker.startUploadSession();

    // Step 1: Initiate multipart upload
    const startBody = { contentType, patientLabel };
    if (profileId) startBody.profileId = profileId;

    const startRes = await apiFetchWithRefresh('/upload-multipart/start', {
        method: 'POST',
        body: JSON.stringify(startBody),
    });

    if (!startRes.ok) {
        const errBody = await startRes.text().catch(() => '');
        throw new Error(`Failed to start multipart upload (HTTP ${startRes.status}): ${errBody}`);
    }

    const { uploadId, s3Key } = await startRes.json();
    logger.info('UPLOAD', 'Multipart upload initiated', { uploadId, s3Key, numParts });

    // Step 2: Upload each chunk with retry
    const completedParts = [];
    let totalUploaded = 0;

    for (let partNum = 1; partNum <= numParts; partNum++) {
        const start = (partNum - 1) * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, totalSize);
        const chunkBlob = audioBlob.slice(start, end);

        let etag = null;
        let lastError = null;
        let realAttempts = 0; // Only count non-background failures
        const MAX_BG_RETRIES = 10; // Allow many background-caused retries (doctor switching tabs)
        let bgRetries = 0;
        let urlRefreshRetries = 0; // FIX 4: 403/expired URL retries (don't count against real limit)
        const MAX_URL_REFRESH_RETRIES = 3;

        while (realAttempts < MAX_CHUNK_RETRIES) {
            try {
                // Get presigned URL for this part
                const urlRes = await apiFetchWithRefresh('/upload-multipart/url', {
                    method: 'POST',
                    body: JSON.stringify({ uploadId, s3Key, partNumber: partNum }),
                });

                if (!urlRes.ok) {
                    throw new Error(`Failed to get part URL (HTTP ${urlRes.status})`);
                }

                const { url } = await urlRes.json();

                // Upload chunk with XHR (Safari-safe, has progress)
                const chunkBaseProgress = totalUploaded;
                etag = await uploadChunkXHR(url, chunkBlob, (loaded, total) => {
                    const overallProgress = (chunkBaseProgress + loaded) / totalSize;
                    if (onProgress) onProgress(overallProgress);
                });

                // Success â€” record this part
                completedParts.push({ PartNumber: partNum, ETag: etag });
                totalUploaded += chunkBlob.size;
                logger.info('UPLOAD', `Part ${partNum}/${numParts} uploaded`, {
                    realAttempts, bgRetries, urlRefreshRetries, etag: etag?.substring(0, 10),
                });
                break; // Exit retry loop

            } catch (err) {
                lastError = err;
                // FIX 4: Detect HTTP 403 (expired presigned URL) â€” get fresh URL and retry
                // without counting against the normal retry limit
                const is403 = err.message && err.message.includes('HTTP 403');
                if (is403 && urlRefreshRetries < MAX_URL_REFRESH_RETRIES) {
                    urlRefreshRetries++;
                    logger.info('UPLOAD', `Part ${partNum} got 403 (expired URL), refreshing URL (attempt ${urlRefreshRetries}/${MAX_URL_REFRESH_RETRIES})`, {
                        error: err.message,
                    });
                    // Brief delay before retry to avoid hammering
                    await new Promise(r => setTimeout(r, 500));
                    // Continue loop â€” will get a fresh presigned URL at the top
                } else if (err._backgroundAbort) {
                    // Background-caused abort â€” don't count toward real retries
                    bgRetries++;
                    logger.info('UPLOAD', `Part ${partNum} background-abort #${bgRetries}, retrying immediately`, {
                        error: err.message,
                    });
                    if (bgRetries >= MAX_BG_RETRIES) {
                        // Safety valve: too many background retries, count as real failure
                        realAttempts++;
                        logger.warn('UPLOAD', `Part ${partNum} exceeded max background retries`, { bgRetries });
                    }
                    // Retry immediately (no delay â€” user just returned to foreground)
                } else {
                    // Real failure (network error, non-403 HTTP error, foreground timeout)
                    realAttempts++;
                    logger.warn('UPLOAD', `Part ${partNum} real attempt ${realAttempts} failed`, { error: err.message });
                    if (realAttempts < MAX_CHUNK_RETRIES) {
                        // Wait before retry: 2s, 4s
                        await new Promise(r => setTimeout(r, realAttempts * 2000));
                    }
                }
            }
        }

        if (!etag) {
            // All retries exhausted for this chunk
            BackgroundTracker.endUploadSession();
            logger.error('UPLOAD', `Part ${partNum} failed after ${realAttempts} real attempts (${bgRetries} bg retries)`, lastError);
            throw new Error(`Upload failed on chunk ${partNum}/${numParts} after ${realAttempts} retries: ${lastError?.message}`);
        }
    }

    // Step 3: Complete the multipart upload
    logger.info('UPLOAD', 'Completing multipart upload', { parts: completedParts.length });
    const completeRes = await apiFetchWithRefresh('/upload-multipart/complete', {
        method: 'POST',
        body: JSON.stringify({ uploadId, s3Key, parts: completedParts }),
    });

    if (!completeRes.ok) {
        const errBody = await completeRes.text().catch(() => '');
        throw new Error(`Failed to complete multipart upload (HTTP ${completeRes.status}): ${errBody}`);
    }

    const completeData = await completeRes.json();
    // FIX N7c (E21): this logged the real audio S3 key â€” which embeds the patient
    // label â€” into the log buffer, and UPLOAD-category logs are uploaded with
    // feedback tickets and diagnostic reports. sanitizeLogs() now strips it at the
    // sink, but Â§4.2 requires PHI never be captured in the first place. Whether we
    // got a key back is the only diagnostic signal actually needed here.
    // Revert note: to roll back FIX N7c, restore
    //   { s3Key: completeData.s3Key }
    logger.info('UPLOAD', 'Multipart upload completed successfully', { keyReturned: !!completeData.s3Key });
    BackgroundTracker.endUploadSession();

    if (onProgress) onProgress(1.0);
    return { s3Key: completeData.s3Key };
}

/**
 * Upload a small file using XMLHttpRequest (Safari-compatible single PUT with progress).
 * v2.9.7: No longer used in primary path (simple fetch is primary). Kept as utility.
 * Background-aware: uses foreground-only timeout and aborts stale XHR on tab return.
 * Returns on success, throws on failure.
 */
export function uploadSingleXHR(uploadUrl, audioBlob, contentType, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl, true);
        xhr.setRequestHeader('Content-Type', contentType);

        // v2.9.7: Relaxed foreground timeout â€” 300s minimum
        const FOREGROUND_TIMEOUT = Math.max(300000, 300000 + Math.ceil(audioBlob.size / (5 * 1024 * 1024)) * 60000);
        let foregroundElapsed = 0;
        let lastTickTime = Date.now();
        let settled = false;

        // Tick-based foreground timer
        const chunkTimer = setInterval(() => {
            if (!BackgroundTracker.isBackground()) {
                const now = Date.now();
                foregroundElapsed += (now - lastTickTime);
                lastTickTime = now;
                if (foregroundElapsed >= FOREGROUND_TIMEOUT) {
                    clearInterval(chunkTimer);
                    BackgroundTracker.offForeground(onForegroundReturn);
                    if (!settled) {
                        settled = true;
                        xhr.abort();
                        reject(new Error('Upload timed out (foreground timeout)'));
                    }
                }
            } else {
                lastTickTime = Date.now();
            }
        }, 1000);

        // v2.9.7: Only abort if backgrounded > 120s, with 15s grace period
        function onForegroundReturn(bgDurationMs) {
            if (bgDurationMs > BG_ABORT_THRESHOLD_MS && !settled) {
                logger.info('BG_TRACKER', 'Long background on single XHR, starting 15s grace period', { bgDurationMs });
                setTimeout(() => {
                    if (!settled) {
                        logger.info('BG_TRACKER', 'Grace period expired, aborting single XHR', { bgDurationMs });
                        settled = true;
                        clearInterval(chunkTimer);
                        BackgroundTracker.offForeground(onForegroundReturn);
                        xhr.abort();
                        const err = new Error('Single upload XHR aborted after long background');
                        err._backgroundAbort = true;
                        reject(err);
                    }
                }, BG_FOREGROUND_GRACE_MS);
            }
        }
        BackgroundTracker.onForeground(onForegroundReturn);

        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onProgress) {
                onProgress(e.loaded / e.total);
            }
        };

        xhr.onload = () => {
            clearInterval(chunkTimer);
            BackgroundTracker.offForeground(onForegroundReturn);
            if (settled) return;
            settled = true;
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                reject(new Error(`Upload failed (HTTP ${xhr.status})`));
            }
        };

        xhr.onerror = () => {
            clearInterval(chunkTimer);
            BackgroundTracker.offForeground(onForegroundReturn);
            if (settled) return;
            settled = true;
            reject(new Error('Network error during upload'));
        };

        xhr.ontimeout = () => {
            clearInterval(chunkTimer);
            BackgroundTracker.offForeground(onForegroundReturn);
            if (settled) return;
            settled = true;
            reject(new Error('Upload timed out (XHR native timeout)'));
        };

        // Generous native timeout as safety net
        xhr.timeout = 300000; // 5 min absolute max

        xhr.send(audioBlob);
    });
}

/**
 * FIX A3: resolve the real content type. Order: the blob's own type (set from the
 * recorder's mimeType, codec parameter included) â†’ the metadata we retained â†’
 * the file extension for user-picked files. Returns '' when genuinely unknown so
 * the caller can fail loudly instead of mislabelling WebM as MP4.
 */
export function resolveContentType(blob, audioMeta) {
    const fromBlob = (blob && blob.type ? blob.type : '').trim();
    if (fromBlob) return fromBlob;
    const fromMeta = (audioMeta && audioMeta.mimeType ? audioMeta.mimeType : '').trim();
    if (fromMeta) return fromMeta;
    const name = (blob instanceof File && blob.name) ? blob.name.toLowerCase() : '';
    const extMap = {
        '.m4a': 'audio/mp4', '.mp4': 'audio/mp4', '.webm': 'audio/webm',
        '.wav': 'audio/wav', '.mp3': 'audio/mpeg', '.ogg': 'audio/ogg',
        '.oga': 'audio/ogg', '.aac': 'audio/aac', '.flac': 'audio/flac',
        '.amr': 'audio/amr', '.caf': 'audio/x-caf',
    };
    for (const ext in extMap) {
        if (name.endsWith(ext)) return extMap[ext];
    }
    return '';
}

/**
 * FIX A3: `audioMeta` is optional and additive â€” { mimeType, segmentCount,
 * generationCount, droppedSegments, blobSize }. It lets /process record what was
 * actually uploaded so a Transcribe failure is diagnosable.
 */
export async function uploadAndProcess(audioBlob, source, audioMeta) {
    // Acquire wake lock for the entire upload+processing phase.
    // Prevents Safari from suspending the network request when the user switches apps.
    await requestWakeLock();
    logger.info('UPLOAD', 'Wake lock acquired for upload');

    const statusEl = source === 'upload' ? 'uploadStatus' : 'recordStatus';
    const progressEl = source === 'upload' ? 'fileUploadProgress' : 'uploadProgress';
    const fillEl = source === 'upload' ? 'fileUploadFill' : 'uploadFill';
    const labelEl = source === 'upload' ? 'uploadPatientLabel' : 'patientLabel';
    const patientLabel = document.getElementById(labelEl).value.trim() || 'visit';

    // FIX: Capture profileId NOW — at this point the user just finished recording
    // (which requires PIN-verified profile), so it's guaranteed valid.
    // Reading it later (after 11s+ upload) risks a null due to ProfileStore being
    // cleared by a racing async operation or sessionStorage eviction.
    const capturedProfileId = currentProfileId;

    // Save blob for retry
    state.pendingUploadBlob = audioBlob;
    state.pendingUploadSource = source;
    state.pendingUploadMeta = audioMeta || null; // FIX A3: metadata must survive a retry

    // v2.9.11: PRE-UPLOAD DIAGNOSTIC â€” captures blob readability, browser, network state
    let preDiag = null;
    try {
        preDiag = await preUploadDiagnostic(audioBlob, source, logger);
    } catch (diagErr) {
        logger.warn('UPLOAD', 'Pre-upload diagnostic failed (non-fatal)', { error: diagErr.message });
        preDiag = { timestamp: new Date().toISOString(), phase: 'pre-upload', source, diagError: diagErr.message };
    }

    // If blob is unreadable, warn early but still attempt upload (let it fail with full telemetry)
    if (preDiag && preDiag.blobReadable === false) {
        logger.error('UPLOAD', 'BLOB UNREADABLE â€” upload will likely fail', null, {
            blobReadError: preDiag.blobReadError,
            blobConstructor: preDiag.blobConstructor,
            source: preDiag.source,
            isSafari: preDiag.isSafari,
        });
    }

    logger.info('UPLOAD', 'Upload started', {
        blobSize: audioBlob.size,
        contentType: audioBlob.type || (audioMeta && audioMeta.mimeType) || '(unknown)', // FIX A3: no audio/mp4 guess
        source,
        patient: '[patient]',
        online: navigator.onLine,
        connectionType: navigator.connection ? navigator.connection.effectiveType : 'unknown',
        downlink: navigator.connection ? navigator.connection.downlink : null,
        rtt: navigator.connection ? navigator.connection.rtt : null,
    });
    const uploadFlowStart = Date.now();

    // SAFETY NET: Session already saved in SessionStore during recording
    // For file uploads, create a session entry
    if (source === 'upload' && !state.currentSessionDbId) {
        try {
            const sessionRecord = {
                sessionId: crypto.randomUUID(),
                status: 'uploading',
                patientLabel: patientLabel.replace(/[^a-zA-Z0-9\s\-_]/g, ''),
                segments: [audioBlob],
                mimeType: audioBlob.type || 'audio/webm',
                startedAt: new Date().toISOString(),
                lastSavedAt: new Date().toISOString(),
                totalDurationMs: 0,
                uploadedNoteId: null,
            };
            state.currentSessionDbId = await SessionStore.create(sessionRecord);
            logger.info('UPLOAD', 'Upload session saved to SessionStore', { id: state.currentSessionDbId });
        } catch (dbErr) {
            logger.warn('UPLOAD', 'SessionStore save failed (non-fatal)', { error: dbErr.message });
        }
    } else if (state.currentSessionDbId) {
        try { await SessionStore.update(state.currentSessionDbId, { status: 'uploading' }); } catch (e) {}
    }

    try {
        setStatus(statusEl, 'Preparing upload...', 'uploading');
        showProgress(progressEl, fillEl, 10);
        // FIX A3: the old `|| 'audio/mp4'` fallback mislabelled WebM as MP4, which is
        // one way a valid recording became "input media file isn't valid" downstream.
        // Derive the type from the recorder metadata, then the filename, then fail loudly.
        const contentType = resolveContentType(audioBlob, audioMeta);
        if (!contentType) {
            logger.error('UPLOAD', 'Cannot determine audio content type â€” refusing to mislabel the upload', null, {
                blobSize: audioBlob.size,
                blobType: audioBlob.type || '',
                fileName: (audioBlob instanceof File) ? audioBlob.name : null,
                metaMimeType: (audioMeta && audioMeta.mimeType) || null,
            });
            throw new Error('Could not determine the audio format for this recording. Your audio is saved under Recent Recordings â€” use Save Audio and send it to support.');
        }

        logger.info('UPLOAD', 'v2.9.8 upload strategy: simple fetch PUT primary, multipart fallback on TypeError', { contentType, blobSize: audioBlob.size, online: navigator.onLine });
        const s3PutStart = Date.now();
        let s3Key;

        // Upload telemetry: track if the page was backgrounded during upload.
        // Mobile Safari suspends network requests when backgrounded, so this is
        // a key diagnostic signal. Listener is removed after upload completes.
        let uploadWasBackgrounded = false;
        let uploadRetryCount = 0;
        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                uploadWasBackgrounded = true;
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        // Step 1: Get presigned URL (used by both primary and single-PUT path)
        setStatus(statusEl, 'Getting upload URL...', 'uploading');
        const presignStart = Date.now();
        const uploadBody = { patientLabel, contentType };
        if (capturedProfileId) uploadBody.profileId = capturedProfileId;
        const urlController = new AbortController();
        const urlTimeout = setTimeout(() => urlController.abort(), 30000);
        let urlRes;
        try {
            urlRes = await apiFetchWithRefresh('/upload-url', {
                method: 'POST',
                body: JSON.stringify(uploadBody),
                signal: urlController.signal,
            });
        } finally {
            clearTimeout(urlTimeout);
        }
        if (!urlRes.ok) {
            const errBody = await urlRes.text().catch(() => '');
            // FIX N7b (E21): the raw response body was captured verbatim into the
            // log buffer, which feedback tickets and /diagnostics/report upload to
            // S3. sanitizeLogs() scrubs it at the sink, but Â§4.2 requires
            // redaction AT THE POINT OF CAPTURE â€” a raw body must never enter the
            // buffer in the first place. /upload-url replies can quote the audio
            // key, and an audio key embeds the patient label.
            // Revert note: to roll back FIX N7b, replace redactForLogs(errBody, 500)
            // with errBody on the next line.
            logger.error('UPLOAD', 'Presigned URL request failed', new Error('Failed to get upload URL'), { httpStatus: urlRes.status, responseBody: redactForLogs(errBody, 500), failurePoint: 'upload' });
            if (urlRes.status === 401) {
                throw new Error('Session expired. Your recording is saved safely. Please sign in again.');
            }
            throw new Error('Failed to get upload URL (HTTP ' + urlRes.status + ')');
        }
        const urlData = await urlRes.json();
        s3Key = urlData.s3Key;
        const presignEnd = Date.now();
        logger.info('UPLOAD', 'Presigned URL received', { s3Key, durationMs: presignEnd - presignStart });

        // Step 2: v2.11.0 upload strategy â€” size-based routing
        // Files > 5MB: multipart upload (per-chunk progress, fresh URLs per chunk, resume capability)
        // Files â‰¤ 5MB: simple fetch PUT (fast, reliable for small recordings)
        const useMultipart = audioBlob.size > MULTIPART_THRESHOLD;
        logger.info('UPLOAD', `v2.11.0 upload strategy: ${useMultipart ? 'multipart (large file)' : 'simple fetch PUT (small file)'}`, { contentType, blobSize: audioBlob.size, threshold: MULTIPART_THRESHOLD, online: navigator.onLine });

        // Network connectivity check before upload
        try {
            const checkStart = Date.now();
            const headRes = await fetch(API_BASE + '/health', { method: 'HEAD', signal: AbortSignal.timeout(5000) }).catch(() => null);
            addUploadProgressLog({
                timestamp: new Date().toISOString(),
                type: 'network-check',
                reachable: !!(headRes && headRes.ok),
                latencyMs: Date.now() - checkStart,
                online: navigator.onLine,
                connectionType: navigator.connection ? navigator.connection.effectiveType : 'unknown',
            });
            logger.info('UPLOAD', 'Pre-upload network check', { reachable: !!(headRes && headRes.ok), latencyMs: Date.now() - checkStart });
        } catch (checkErr) {
            addUploadProgressLog({ timestamp: new Date().toISOString(), type: 'network-check', reachable: false, error: checkErr.message });
        }

        let fetchUploadSucceeded = false;

        if (useMultipart) {
            // â”€â”€ MULTIPART PRIMARY PATH (files > 5MB) â”€â”€
            // Per-chunk progress, fresh presigned URLs per chunk, automatic retry on 403
            setStatus(statusEl, 'Uploading audio...', 'uploading');
            showProgress(progressEl, fillEl, 15);
            const multipartStart = Date.now();
            let lastSpeedUpdate = 0;

            const multipartResult = await uploadMultipart(audioBlob, contentType, patientLabel, capturedProfileId, (progress) => {
                const pct = 15 + Math.round(progress * 33); // 15% to 48%
                showProgress(progressEl, fillEl, pct);

                // Calculate speed and ETA for user-friendly display
                const elapsed = Date.now() - multipartStart;
                const bytesUploaded = Math.round(progress * audioBlob.size);
                const totalMB = (audioBlob.size / (1024 * 1024)).toFixed(1);
                const uploadedMB = (bytesUploaded / (1024 * 1024)).toFixed(1);
                const pctDisplay = Math.round(progress * 100);

                // Only update speed/ETA every 500ms to avoid flickering
                if (elapsed - lastSpeedUpdate > 500 || progress >= 1) {
                    lastSpeedUpdate = elapsed;
                    if (elapsed > 1000 && progress > 0.01) {
                        const speedBps = bytesUploaded / (elapsed / 1000);
                        const speedMBps = (speedBps / (1024 * 1024)).toFixed(1);
                        const remainingBytes = audioBlob.size - bytesUploaded;
                        const etaSeconds = Math.max(1, Math.round(remainingBytes / speedBps));
                        const etaDisplay = etaSeconds > 60
                            ? `${Math.floor(etaSeconds / 60)}m ${etaSeconds % 60}s`
                            : `${etaSeconds}s`;
                        setStatus(statusEl, `Uploading... ${pctDisplay}% (${uploadedMB} / ${totalMB} MB) \u2014 ${speedMBps} MB/s \u2014 ~${etaDisplay} remaining`, 'uploading');
                    } else {
                        setStatus(statusEl, `Uploading... ${pctDisplay}% (${uploadedMB} / ${totalMB} MB)`, 'uploading');
                    }
                }
            });

            s3Key = multipartResult.s3Key;
            fetchUploadSucceeded = true;
            const multipartDuration = Date.now() - multipartStart;
            const uploadSpeed = Math.round(audioBlob.size / 1024 / (multipartDuration / 1000));
            showProgress(progressEl, fillEl, 48);
            logger.info('UPLOAD', 'Multipart upload succeeded (primary path)', { durationMs: multipartDuration, uploadSpeedKBps: uploadSpeed, blobSize: audioBlob.size });

            const resultTelemetry = createUploadResultTelemetry({
                success: true,
                method: 'multipart',
                durationMs: multipartDuration,
                blobSize: audioBlob.size,
                usedFallback: false,
            });
            addUploadDiagnostic({ preDiag, result: resultTelemetry });

        } else {
            // â”€â”€ SIMPLE XHR PUT PATH (files â‰¤ 5MB) â€” BUG 3 FIX: shows upload speed/ETA â”€â”€
            setStatus(statusEl, 'Uploading audio...', 'uploading');
            showProgress(progressEl, fillEl, 30);

            let uploadHeartbeat = null;
            try {
                const fetchStartTime = Date.now();
                let heartbeatCount = 0;
                uploadHeartbeat = setInterval(() => {
                    heartbeatCount++;
                    const elapsed = Date.now() - fetchStartTime;
                    const entry = {
                        timestamp: new Date().toISOString(),
                        type: 'upload-heartbeat',
                        heartbeat: heartbeatCount,
                        elapsedMs: elapsed,
                        elapsedSec: Math.round(elapsed / 1000),
                        online: navigator.onLine,
                        visibilityState: document.visibilityState,
                        connectionType: navigator.connection ? navigator.connection.effectiveType : 'unknown',
                        downlink: navigator.connection ? navigator.connection.downlink : null,
                        rtt: navigator.connection ? navigator.connection.rtt : null,
                    };
                    addUploadProgressLog(entry);
                    logger.info('UPLOAD', `Heartbeat #${heartbeatCount}: ${Math.round(elapsed / 1000)}s elapsed, online=${navigator.onLine}, visibility=${document.visibilityState}`, entry);
                }, 15000);

                // BUG 3 FIX: Use XHR instead of fetch() to get upload progress events
                const putResponse = await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    let lastSpeedUpdate = 0;
                    xhr.upload.onprogress = (evt) => {
                        if (!evt.lengthComputable) return;
                        const progress = evt.loaded / evt.total;
                        const pct = 15 + Math.round(progress * 33); // 15% to 48%
                        showProgress(progressEl, fillEl, pct);

                        const elapsed = Date.now() - fetchStartTime;
                        const totalMB = (evt.total / (1024 * 1024)).toFixed(1);
                        const uploadedMB = (evt.loaded / (1024 * 1024)).toFixed(1);
                        const pctDisplay = Math.round(progress * 100);

                        // Only update speed/ETA every 500ms to avoid flickering
                        if (elapsed - lastSpeedUpdate > 500 || progress >= 1) {
                            lastSpeedUpdate = elapsed;
                            if (elapsed > 500 && progress > 0.01) {
                                const speedBps = evt.loaded / (elapsed / 1000);
                                const speedMBps = (speedBps / (1024 * 1024)).toFixed(1);
                                const remainingBytes = evt.total - evt.loaded;
                                const etaSeconds = Math.max(1, Math.round(remainingBytes / speedBps));
                                const etaDisplay = etaSeconds > 60
                                    ? `${Math.floor(etaSeconds / 60)}m ${etaSeconds % 60}s`
                                    : `${etaSeconds}s`;
                                setStatus(statusEl, `Uploading... ${pctDisplay}% (${uploadedMB} / ${totalMB} MB) \u2014 ${speedMBps} MB/s \u2014 ~${etaDisplay} remaining`, 'uploading');
                            } else {
                                setStatus(statusEl, `Uploading... ${pctDisplay}% (${uploadedMB} / ${totalMB} MB)`, 'uploading');
                            }
                        }
                    };
                    xhr.onload = () => {
                        resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, statusText: xhr.statusText, headers: xhr.getAllResponseHeaders() });
                    };
                    xhr.onerror = () => {
                        const err = new TypeError('Network request failed');
                        reject(err);
                    };
                    xhr.ontimeout = () => {
                        const err = new TypeError('Upload request timed out');
                        reject(err);
                    };
                    xhr.open('PUT', urlData.uploadUrl);
                    xhr.setRequestHeader('Content-Type', contentType);
                    xhr.send(audioBlob);
                });

                clearInterval(uploadHeartbeat);

                if (!putResponse.ok) {
                    const s3Err = new Error(`S3 PUT failed (HTTP ${putResponse.status})`);
                    s3Err._httpStatus = putResponse.status;
                    s3Err._statusText = putResponse.statusText;
                    s3Err._responseHeaders = null;
                    throw s3Err;
                }

                fetchUploadSucceeded = true;
                const uploadDuration = Date.now() - s3PutStart;
                const uploadSpeed = Math.round(audioBlob.size / 1024 / (uploadDuration / 1000));
                showProgress(progressEl, fillEl, 48);
                addUploadProgressLog({
                    timestamp: new Date().toISOString(),
                    type: 'upload-complete',
                    method: 'fetch-put',
                    durationMs: uploadDuration,
                    speedKBps: uploadSpeed,
                    blobSize: audioBlob.size,
                    heartbeats: heartbeatCount,
                });
                logger.info('UPLOAD', 'Simple fetch PUT succeeded', { durationMs: uploadDuration, uploadSpeedKBps: uploadSpeed, heartbeats: heartbeatCount });

                const resultTelemetry = createUploadResultTelemetry({
                    success: true,
                    method: 'fetch-put',
                    durationMs: uploadDuration,
                    blobSize: audioBlob.size,
                    usedFallback: false,
                });
                addUploadDiagnostic({ preDiag, result: resultTelemetry });
            } catch (fetchErr) {
                clearInterval(uploadHeartbeat);
                // On TypeError (Safari background freeze) or 403 (expired URL), fall back to multipart
                if (fetchErr instanceof TypeError || (fetchErr._httpStatus === 403)) {
                    const reason = fetchErr instanceof TypeError
                        ? 'TypeError: ' + fetchErr.message
                        : 'HTTP 403 (expired presigned URL)';
                    logger.warn('UPLOAD', 'Simple fetch PUT failed â€” falling back to multipart', {
                        reason,
                        durationMs: Date.now() - s3PutStart,
                        blobSize: audioBlob.size,
                        online: navigator.onLine,
                    });

                    const fetchFailTelemetry = createUploadResultTelemetry({
                        success: false,
                        method: 'fetch-put',
                        durationMs: Date.now() - s3PutStart,
                        blobSize: audioBlob.size,
                        errorName: fetchErr.name,
                        errorMessage: fetchErr.message,
                        errorType: fetchErr instanceof TypeError ? 'TypeError' : 'HTTP403',
                        lastProgressBytes: 0,
                        usedFallback: true,
                        fallbackReason: reason,
                    });

                    setStatus(statusEl, 'Retrying upload (chunked)...', 'uploading');
                    uploadRetryCount++;
                    const multipartStart = Date.now();
                    let fallbackLastSpeedUpdate = 0;
                    const multipartResult = await uploadMultipart(audioBlob, contentType, patientLabel, capturedProfileId, (progress) => {
                        const pct = 15 + Math.round(progress * 33);
                        showProgress(progressEl, fillEl, pct);
                        // BUG 3 FIX: Show speed/ETA in multipart fallback path
                        const elapsed = Date.now() - multipartStart;
                        const bytesUploaded = Math.round(progress * audioBlob.size);
                        const totalMB = (audioBlob.size / (1024 * 1024)).toFixed(1);
                        const uploadedMB = (bytesUploaded / (1024 * 1024)).toFixed(1);
                        const pctDisplay = Math.round(progress * 100);

                        if (elapsed - fallbackLastSpeedUpdate > 500 || progress >= 1) {
                            fallbackLastSpeedUpdate = elapsed;
                            if (elapsed > 1000 && progress > 0.01) {
                                const speedBps = bytesUploaded / (elapsed / 1000);
                                const speedMBps = (speedBps / (1024 * 1024)).toFixed(1);
                                const remainingBytes = audioBlob.size - bytesUploaded;
                                const etaSeconds = Math.max(1, Math.round(remainingBytes / speedBps));
                                const etaDisplay = etaSeconds > 60
                                    ? `${Math.floor(etaSeconds / 60)}m ${etaSeconds % 60}s`
                                    : `${etaSeconds}s`;
                                setStatus(statusEl, `Uploading... ${pctDisplay}% (${uploadedMB} / ${totalMB} MB) \u2014 ${speedMBps} MB/s \u2014 ~${etaDisplay} remaining`, 'uploading');
                            } else {
                                setStatus(statusEl, `Uploading... ${pctDisplay}% (${uploadedMB} / ${totalMB} MB)`, 'uploading');
                            }
                        }
                    });
                    s3Key = multipartResult.s3Key;
                    fetchUploadSucceeded = true;
                    const multipartDuration = Date.now() - multipartStart;
                    logger.info('UPLOAD', 'Multipart fallback succeeded', { durationMs: multipartDuration, uploadSpeedKBps: Math.round(audioBlob.size / 1024 / (multipartDuration / 1000)) });

                    const fallbackResultTelemetry = createUploadResultTelemetry({
                        success: true,
                        method: 'multipart',
                        durationMs: multipartDuration,
                        blobSize: audioBlob.size,
                        usedFallback: true,
                        fallbackReason: reason,
                    });
                    addUploadDiagnostic({ preDiag, fetchFailure: fetchFailTelemetry, result: fallbackResultTelemetry });
                } else {
                    // Non-recoverable error â€” re-throw
                    const failTelemetry = createUploadResultTelemetry({
                        success: false,
                        method: 'fetch-put',
                        durationMs: Date.now() - s3PutStart,
                        blobSize: audioBlob.size,
                        errorName: fetchErr.name,
                        errorMessage: fetchErr.message,
                        errorType: fetchErr.name === 'AbortError' ? 'AbortError' : 'Other',
                        httpStatus: fetchErr._httpStatus || null,
                        s3ResponseStatus: fetchErr._httpStatus || null,
                        s3ResponseStatusText: fetchErr._statusText || null,
                        s3ResponseHeaders: fetchErr._responseHeaders || null,
                    });
                    addUploadDiagnostic({ preDiag, result: failTelemetry });
                    throw fetchErr;
                }
            }
        }

        // Audio is safely in S3 at this point
        // Remove the backgrounding detection listener â€” upload phase is done.
        document.removeEventListener('visibilitychange', onVisibilityChange);

        // Build upload telemetry metrics â€” all non-PHI device/network data.
        const uploadEndTime = Date.now();
        const uploadDurationMs = uploadEndTime - s3PutStart;
        const uploadMetrics = {
            uploadDurationMs,
            uploadSpeedBps: uploadDurationMs > 0 ? Math.round(audioBlob.size / (uploadDurationMs / 1000)) : 0,
            fileSizeBytes: audioBlob.size,
            // Recording duration â€” how long the user actually recorded audio (ms).
            // Only meaningful for source='record'; file uploads get 0.
            recordingDurationMs: (source === 'record' && state.elapsedBeforePause > 0) ? Math.round(state.elapsedBeforePause) : 0,
            audioSizeBytes: audioBlob.size,
            uploadMethod: (useMultipart || uploadRetryCount > 0) ? 'multipart' : 'simple-put',
            connectionType: (navigator.connection && navigator.connection.effectiveType) || 'unknown',
            connectionDownlink: (navigator.connection && navigator.connection.downlink) || 0,
            connectionRtt: (navigator.connection && navigator.connection.rtt) || 0,
            wasBackgrounded: uploadWasBackgrounded ? 'true' : 'false',
            wakeLockActive: state.wakeLock ? 'true' : 'false',
            retryCount: typeof uploadRetryCount === 'number' ? uploadRetryCount : 0,
            presignDurationMs: presignEnd - presignStart,
            totalFlowDurationMs: uploadEndTime - uploadFlowStart,
            appVersion: APP_VERSION,
        };

        setStatus(statusEl, 'Audio uploaded \u2713 \u2014 Starting transcription...', 'uploading');
        showProgress(progressEl, fillEl, 50);
        logger.info('UPLOAD', 'Starting processing request (async)', { s3Key, audioConfirmedInS3: true, timeSinceFlowStart: Date.now() - uploadFlowStart });
        const processStart = Date.now();
        const processBody = { s3Key, patientLabel };
        if (capturedProfileId) processBody.profileId = capturedProfileId;
        // FEAT-002: carry the linked patientId into the create request ONLY when a
        // patient was selected. record vs upload read their own picker. No selection
        // -> key omitted, so the request body is byte-identical to the pre-feature flow.
        const linkedPatientId = source === 'upload' ? getUploadPatientId() : getRecordPatientId();
        if (linkedPatientId) processBody.patientId = linkedPatientId;
        const apptType = getSelectedAppointmentType();
        if (apptType) processBody.appointmentType = apptType;
        // Quick Note mode: send recordingMode to backend
        if (state.noteMode === 'quick_note') {
            processBody.recordingMode = 'quick_note';
        }
        // FIX A3: additive audio provenance so the job record explains a failure.
        // No PHI in any of these fields.
        processBody.mimeType = contentType;
        processBody.blobSize = audioBlob.size;
        processBody.segmentCount = (audioMeta && typeof audioMeta.segmentCount === 'number') ? audioMeta.segmentCount : 1;
        processBody.recorderGenerationCount = (audioMeta && typeof audioMeta.generationCount === 'number') ? audioMeta.generationCount : 1;
        if (audioMeta && audioMeta.droppedSegments) {
            processBody.droppedSegments = audioMeta.droppedSegments;
            processBody.droppedBytes = audioMeta.droppedBytes || 0;
        }
        // Upload telemetry â€” non-PHI device/network metrics for diagnosing slow uploads.
        processBody.uploadMetrics = uploadMetrics;

        // Short 30s timeout â€” /process now returns immediately with jobId
        const processController = new AbortController();
        const processTimeout = setTimeout(() => processController.abort(), 30000);

        let procRes;
        let processTimedOut = false;
        try {
            procRes = await apiFetchWithRefresh('/process', {
                method: 'POST',
                body: JSON.stringify(processBody),
                signal: processController.signal,
            });
        } catch (processErr) {
            const isTimeout = processErr.name === 'AbortError';
            const isNetwork = isNetworkError(processErr);
            if (isTimeout || isNetwork) {
                processTimedOut = true;
                logger.warn('UPLOAD', 'Process request timed out or network error (audio is safe in S3)', {
                    isTimeout, isNetwork, error: processErr.message, s3Key,
                    processDurationMs: Date.now() - processStart,
                    online: navigator.onLine,
                });
            } else {
                throw processErr;
            }
        } finally {
            clearTimeout(processTimeout);
        }

        // Handle timeout gracefully â€” audio is safe, Lambda likely still processing
        if (processTimedOut) {
            state.pendingUploadBlob = null;
            state.pendingUploadSource = null;
            hideRetryUI();

            if (state.currentSessionDbId) {
                try { await SessionStore.update(state.currentSessionDbId, { status: 'processing' }); } catch (dbErr) {}
                state.currentSessionDbId = null;
            }
            state.currentSessionId = null;

            showProgress(progressEl, fillEl, 85);
            setStatus(statusEl, 'Processing is taking longer than usual. Your audio is safe \u2014 the note will appear in the Notes tab when ready.', 'uploading');

            const container = document.getElementById('newRecordingActions');
            if (container) {
                container.classList.remove('hidden');
                container.innerHTML = `
                    <button class="btn" data-action="click-notes-tab">
                        \ud83d\udcdd Check Notes
                    </button>
                    <button class="btn btn-outline" data-action="reset-for-new-recording">
                        \ud83c\udfa4 New Recording
                    </button>
                `;
            }
            logger.info('UPLOAD', 'Showed graceful timeout UI â€” audio safe in S3');
            return;
        }

        if (!procRes.ok) {
            const errBody = await procRes.text().catch(() => '');
            // FIX N7b (E21): same class as the /upload-url capture above, and the
            // more important of the two â€” this is the /process path whose 404 body
            // used to be f"Audio file not found: {s3_key}" (handler.py FIX 10).
            // Truncating to 500 chars is not redaction; the key sat in the first
            // ~80. Redact at capture so a server regression cannot refill the buffer.
            // Revert note: to roll back FIX N7b, replace redactForLogs(errBody, 500)
            // with errBody.substring(0, 500) on the next line.
            logger.error('UPLOAD', 'Process request failed', new Error('Failed to start processing'), { httpStatus: procRes.status, responseBody: redactForLogs(errBody, 500), failurePoint: 'transcription' });
            if (procRes.status === 401) {
                throw new Error('Session expired during processing. Your recording is saved safely.');
            }
            throw new Error('Failed to start processing (HTTP ' + procRes.status + ')');
        }
        const procData = await procRes.json();
        logger.info('UPLOAD', 'Process response received', { status: procData.status, jobId: procData.jobId, processDurationMs: Date.now() - processStart, totalFlowDurationMs: Date.now() - uploadFlowStart });
        // Track jobId for feedback diagnostic correlation
        if (procData.jobId) state.lastProcessingJobId = procData.jobId;

        // Upload succeeded â€” clear pending and remove from IndexedDB
        state.pendingUploadBlob = null;
        state.pendingUploadSource = null;
        hideRetryUI();

        if (procData.status === 'COMPLETED') {
            // Mark session as completed immediately
            if (state.currentSessionDbId) {
                try {
                    await SessionStore.update(state.currentSessionDbId, { status: 'completed', uploadedNoteId: procData.jobId || null });
                    logger.info('UPLOAD', 'Session marked completed', { id: state.currentSessionDbId });
                } catch (dbErr) {
                    logger.warn('UPLOAD', 'SessionStore update failed (non-fatal)', { error: dbErr.message });
                }
                state.currentSessionDbId = null;
            }
            state.currentSessionId = null;
            loadRecordingHistory();

            showProgress(progressEl, fillEl, 100);
            setStatus(statusEl, '\u2713 SOAP note generated! Check Notes tab.', 'success');
            logger.info('UPLOAD', 'Processing completed immediately');
            // FEATURE 1: Auto-clear patient tag after successful note generation
            clearPatientTagAfterCompletion(source);
            showNewRecordingBtn(source);
        } else {
            // Processing is async â€” pass the session DB ID to pollForCompletion so it can mark completed
            const pollSessionDbId = state.currentSessionDbId;
            state.currentSessionDbId = null;
            state.currentSessionId = null;
            loadRecordingHistory();

            showProgress(progressEl, fillEl, 70);
            // FIX 6: Show New Recording button immediately so doctor can start next patient
            // Upload is done, processing runs in background â€” don't block the UI
            showNewRecordingBtn(source);
            pollForCompletion(procData.jobId, statusEl, progressEl, fillEl, pollSessionDbId, source);
        }
    } catch (err) {
        // Clean up the upload backgrounding listener if still attached.
        // onVisibilityChange may not be defined if error occurs before its declaration.
        if (typeof onVisibilityChange === 'function') {
            document.removeEventListener('visibilitychange', onVisibilityChange);
        }
        BackgroundTracker.endUploadSession();
        hideProgress(progressEl);
        const networkErr = isNetworkError(err);

        // v2.9.11: Upload failure telemetry for the outer catch
        const outerFailTelemetry = createUploadResultTelemetry({
            success: false,
            method: 'fetch-put',
            durationMs: Date.now() - (uploadFlowStart || Date.now()),
            blobSize: audioBlob.size,
            errorName: err.name,
            errorMessage: err.message,
            errorType: err instanceof TypeError ? 'TypeError' : (err.name === 'AbortError' ? 'AbortError' : 'Other'),
            httpStatus: err._httpStatus || null,
            s3ResponseStatus: err._httpStatus || null,
            s3ResponseStatusText: err._statusText || null,
            s3ResponseHeaders: err._responseHeaders || null,
        });
        // Only add if we haven't already recorded a diagnostic for this attempt
        if (preDiag && !getRecentUploadDiagnostics().some(d => d.preDiag && d.preDiag.timestamp === preDiag.timestamp)) {
            addUploadDiagnostic({ preDiag, result: outerFailTelemetry });
        }

        logger.error('UPLOAD', 'Upload/process failed', err, {
            failurePoint: 'upload',
            isNetworkError: networkErr,
            online: navigator.onLine,
            blobSize: audioBlob.size,
            source,
            savedToIndexedDB: !!currentIndexedDBId,
            connectionInfo: navigator.connection ? { effectiveType: navigator.connection.effectiveType, downlink: navigator.connection.downlink, rtt: navigator.connection.rtt } : null,
        });
        if (err.message.includes('Session expired')) {
            setStatus(statusEl, '\u26a0\ufe0f ' + err.message, 'error');
            showRetryUI(source);
        } else if (networkErr) {
            setStatus(statusEl, '\u26a0\ufe0f Network error \u2014 recording saved safely. Retry when online.', 'error');
            showRetryUI(source);
        } else {
            setStatus(statusEl, 'Error: ' + err.message + ' (recording saved safely)', 'error');
            showRetryUI(source);
        }
    } finally {
        // Release wake lock after upload+processing completes (success or failure)
        await releaseWakeLock();
        logger.info('UPLOAD', 'Wake lock released after upload flow');
    }
}

export function isNetworkError(err) {
    return (err instanceof TypeError && err.message.includes('fetch')) ||
           err.name === 'AbortError' ||
           err.message.includes('network') ||
           err.message.includes('Network') ||
           err.message.includes('Failed to fetch') ||
           err.message.includes('timed out') ||
           err.message.includes('aborted');
}

export function retryUpload() {
    if (!state.pendingUploadBlob) {
        setStatus('recordStatus', 'Nothing to retry.', 'error');
        return;
    }
    logger.info('UPLOAD', 'Retrying upload', { blobSize: state.pendingUploadBlob.size, source: state.pendingUploadSource });
    hideRetryUI();
    uploadAndProcess(state.pendingUploadBlob, state.pendingUploadSource || 'record', state.pendingUploadMeta); // FIX A3
}

export function showRetryUI(source) {
    const container = document.getElementById('retryActions');
    if (container) container.classList.remove('hidden');
    // Session is already persisted in SessionStore
    if (state.currentSessionDbId) {
        SessionStore.update(state.currentSessionDbId, { status: 'pending-upload' }).catch(() => {});
    }
    loadRecordingHistory();
}

export function hideRetryUI() {
    const container = document.getElementById('retryActions');
    if (container) container.classList.add('hidden');
}

export function downloadPendingUpload() {
    if (!state.pendingUploadBlob) {
        showToast('No pending recording to save.', 'warning');
        return;
    }
    const blob = state.pendingUploadBlob;
    const mimeType = blob.type || 'audio/webm';
    let ext = 'webm';
    if (mimeType.includes('mp4') || mimeType.includes('m4a')) ext = 'm4a';
    else if (mimeType.includes('ogg')) ext = 'ogg';

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const filename = `dentscribe-recording-${timestamp}.${ext}`;

    // iOS Safari: <a download> with blob URLs fails (WebKitBlobResource error 1)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (navigator.canShare && navigator.canShare({ files: [new File([blob], filename, { type: mimeType })] })) {
        navigator.share({
            files: [new File([blob], filename, { type: mimeType })],
            title: 'DentScribe Recording',
        }).then(() => {
            logger.info('UPLOAD', 'Pending audio shared via navigator.share', { size: blob.size });
        }).catch(shareErr => {
            if (shareErr.name !== 'AbortError') {
                logger.warn('UPLOAD', 'navigator.share failed for pending audio', { error: shareErr.message });
            }
        });
        return;
    }
    if (isIOS) {
        const url = URL.createObjectURL(blob);
        const w = window.open(url, '_blank');
        if (!w) {
            showToast('Popup blocked — try "Share" from your browser menu.', 'info');
            URL.revokeObjectURL(url);
        } else {
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        }
    } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
    }
    logger.info('UPLOAD', 'Pending audio downloaded', { filename, size: blob.size });
    showToast('Audio file saved.', 'success');
}

export function discardPendingUpload() {
    if (state.currentSessionDbId) {
        SessionStore.remove(state.currentSessionDbId).catch(() => {});
        state.currentSessionDbId = null;
    }
    state.currentSessionId = null;
    state.pendingUploadBlob = null;
    state.pendingUploadSource = null;
    hideRetryUI();
    setStatus('recordStatus', 'Upload discarded. Tap to start a new recording.', '');
    loadRecordingHistory();
}

// --- New Recording button (shown after completion) ---
export function showNewRecordingBtn(source) {
    const container = document.getElementById('newRecordingActions');
    if (container) container.classList.remove('hidden');
}

// FEATURE 1: Clear patient tag input after note generation completes
export function clearPatientTagAfterCompletion(source) {
    // Clear both fields â€” the correct one based on source, plus defensive clear of the other
    if (source === 'upload') {
        const uploadInput = document.getElementById('uploadPatientLabel');
        if (uploadInput) uploadInput.value = '';
    } else {
        const recordInput = document.getElementById('patientLabel');
        if (recordInput) recordInput.value = '';
    }
    // FEAT-002: clear the linked-patient picker alongside the free-text tag so the
    // next visit starts clean (no stale patientId carried into a new note).
    resetUploadPatientPicker();
    resetRecordPatientPicker();
}

// FEATURE 2: Check for duplicate patient tag before recording/upload
export function checkDuplicateTag(tag) {
    const allLoadedNotes = getAllLoadedNotes();
    if (!tag || !allLoadedNotes || allLoadedNotes.length === 0) {
        // No notes loaded yet â€” skip the warning (client-side best-effort)
        return false;
    }
    return allLoadedNotes.some(n => n.patientLabel && n.patientLabel.toLowerCase() === tag.toLowerCase());
}

export async function confirmDuplicateTag(tag) {
    // Returns true if user confirms they want to proceed, false otherwise
    return new Promise((resolve) => {
        const modal = document.getElementById('duplicateTagModal');
        const msgEl = document.getElementById('duplicateTagMessage');
        if (!modal || !msgEl) {
            // Modal not in DOM â€” allow proceeding
            resolve(true);
            return;
        }
        msgEl.textContent = `A note already exists for patient "${tag}". Add another note for the same patient?`;
        modal.classList.add('active');

        const confirmBtn = document.getElementById('duplicateTagConfirm');
        const cancelBtn = document.getElementById('duplicateTagCancel');
        const closeBtn = document.getElementById('duplicateTagClose');
        let resolved = false;

        function cleanup(result) {
            if (resolved) return;
            resolved = true;
            modal.classList.remove('active');
            confirmBtn.removeEventListener('click', onConfirm);
            cancelBtn.removeEventListener('click', onCancel);
            if (closeBtn) closeBtn.removeEventListener('click', onCancel);
            modal.removeEventListener('click', onOverlay);
            resolve(result);
        }
        function onConfirm() { cleanup(true); }
        function onCancel() { cleanup(false); }
        function onOverlay(e) { if (e.target === modal) cleanup(false); }

        confirmBtn.addEventListener('click', onConfirm);
        cancelBtn.addEventListener('click', onCancel);
        if (closeBtn) closeBtn.addEventListener('click', onCancel);
        modal.addEventListener('click', onOverlay);
    });
}

export function hideNewRecordingBtn() {
    const container = document.getElementById('newRecordingActions');
    if (container) container.classList.add('hidden');
}

export function resetForNewRecording() {
    hideNewRecordingBtn();
    hideLiveTranscriptUI();
    hideRetryUI();
    hideInterruptedUI();
    hideRecordingControls();
    resetRecordingSegments(); // FIX A3
    state.elapsedBeforePause = 0;
    state.isPaused = false;
    state.startTime = null;
    state.startTimeMono = null; // FIX A1
    stopTimer();          // FIX A1
    state.recordingStartInProgress = false; // BUG 2 FIX: defensive reset
    state.pendingUploadBlob = null;
    state.pendingUploadSource = null;
    state.pendingUploadMeta = null;
    currentIndexedDBId = null;
    state.currentSessionDbId = null;
    state.currentSessionId = null;
    state.resumingSessionId = null;
    // Reset note mode to default
    state.noteMode = 'appointment';
    const selector = document.getElementById('noteModeSelector');
    const banner = document.getElementById('noteModeBanner');
    if (selector) selector.style.display = '';
    if (banner) banner.style.display = 'none';
    document.getElementById('timer').textContent = '00:00:00';
    document.getElementById('timer').classList.remove('paused');
    document.getElementById('patientLabel').value = '';
    // FEAT-002: full picker state reset for a fresh recording.
    resetRecordPatientPicker();
    resetUploadPatientPicker();
    document.getElementById('recordBtn').style.display = '';
    hideProgress('uploadProgress');
    setStatus('recordStatus', 'Tap to start recording', '');
    loadRecordingHistory();
}

export function pollForCompletion(jobId, statusEl, progressEl, fillEl, sessionDbId, source) {
    let attempts = 0;
    let currentProgress = 50; // Start at 50% (upload is done)
    const MAX_POLLS = 120; // 120 polls Ã— 5s = 10 minutes max
    const pollStartTime = Date.now(); // Track elapsed time for diagnostic logging
    state.lastProcessingJobId = jobId; // Store for feedback diagnostics correlation
    logger.info('UPLOAD', 'Starting poll for completion via /process-status', { jobId, sessionDbId, maxPolls: MAX_POLLS });

    const poll = async () => {
        attempts++;
        try {
            const res = await apiFetchWithRefresh('/process-status', {
                method: 'POST',
                body: JSON.stringify({ jobId }),
            });
            if (!res.ok) {
                logger.warn('UPLOAD', 'Poll /process-status HTTP error, will retry', { attempt: attempts, jobId, status: res.status });
                if (attempts < MAX_POLLS) setTimeout(poll, 5000);
                else showPollTimeout();
                return;
            }
            const data = await res.json();
            const elapsedMs = Date.now() - pollStartTime;
            logger.info('UPLOAD', 'Poll attempt', { attempt: attempts, jobId, status: data.status });
            // Log point 1: Detailed transcription poll telemetry for root-cause analysis
            logger.info('TRANSCRIPTION', 'Poll response', { jobId, status: data.status, attempt: attempts, elapsedMs });

            switch (data.status) {
                case 'COMPLETED':
                    showProgress(progressEl, fillEl, 100);
                    setStatus(statusEl, '\u2713 SOAP note generated! Check Notes tab.', 'success');
                    // BUG 1 FIX: Scroll status into view and pulse so user notices completion
                    const completedStatusEl = document.getElementById(statusEl);
                    if (completedStatusEl) {
                        completedStatusEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                        completedStatusEl.classList.add('status-pulse');
                        setTimeout(() => completedStatusEl.classList.remove('status-pulse'), 3000);
                    }
                    logger.info('UPLOAD', 'Processing completed via polling', { attempts, jobId });
                    if (sessionDbId) {
                        try {
                            await SessionStore.update(sessionDbId, { status: 'completed', uploadedNoteId: jobId });
                            logger.info('UPLOAD', 'Session marked completed after polling', { id: sessionDbId });
                        } catch (dbErr) {
                            logger.warn('UPLOAD', 'SessionStore update after poll failed (non-fatal)', { error: dbErr.message });
                        }
                        loadRecordingHistory();
                    }
                    // FEATURE 1: Auto-clear patient tag after successful note generation
                    clearPatientTagAfterCompletion(source);
                    showNewRecordingBtn(source);
                    return;

                case 'NOTE_FAILED':
                    // Transcript is saved; note-gen failed but is retryable. The
                    // backend auto-retries from the saved transcript on each poll
                    // until it succeeds (COMPLETED) or exhausts retries (FAILED).
                    // Keep polling and DO NOT reset the session to pending-upload.
                    currentProgress = Math.max(currentProgress, Math.min(80 + Math.floor(attempts / 3), 95));
                    showProgress(progressEl, fillEl, currentProgress);
                    setStatus(statusEl, 'Note generation failed \u2014 retrying...', 'uploading');
                    if (sessionDbId) {
                        try { await SessionStore.update(sessionDbId, { status: 'processing' }); } catch (e) {}
                        loadRecordingHistory();
                    }
                    if (attempts < MAX_POLLS) setTimeout(poll, 5000);
                    else showPollTimeout();
                    return;

                case 'FAILED':
                    logger.error('UPLOAD', 'Processing failed', new Error(data.error || 'Unknown processing error'), { jobId, attempts, failurePoint: 'transcription' });
                    setStatus(statusEl, 'Failed: ' + (data.error || 'Unknown error'), 'error');
                    hideProgress(progressEl);
                    if (sessionDbId) {
                        try { await SessionStore.update(sessionDbId, { status: 'pending-upload' }); } catch (dbErr) {}
                        loadRecordingHistory();
                    }
                    showNewRecordingBtn('record');
                    return;

                case 'TRANSCRIBING':
                    currentProgress = Math.max(currentProgress, Math.min(55 + attempts, 75));
                    showProgress(progressEl, fillEl, currentProgress);
                    setStatus(statusEl, 'Transcribing audio... This may take a few minutes for longer recordings.', 'uploading');
                    // BUG 1 FIX: Update session status in IndexedDB so history card shows correct badge
                    if (sessionDbId) {
                        try { await SessionStore.update(sessionDbId, { status: 'processing' }); } catch (e) {}
                        loadRecordingHistory();
                    }
                    break;

                case 'GENERATING_NOTE':
                    currentProgress = Math.max(currentProgress, Math.min(80 + Math.floor(attempts / 3), 95));
                    showProgress(progressEl, fillEl, currentProgress);
                    setStatus(statusEl, 'Generating clinical notes...', 'uploading');
                    // BUG 1 FIX: Update session status in IndexedDB so history card shows correct badge
                    if (sessionDbId) {
                        try { await SessionStore.update(sessionDbId, { status: 'processing' }); } catch (e) {}
                        loadRecordingHistory();
                    }
                    break;

                default:
                    // PROCESSING or any other intermediate status
                    currentProgress = Math.max(currentProgress, Math.min(50 + attempts * 2, 70));
                    showProgress(progressEl, fillEl, currentProgress);
                    setStatus(statusEl, 'Processing... (' + (data.status || 'working') + ')', 'uploading');
                    break;
            }

            if (attempts < MAX_POLLS) {
                setTimeout(poll, 5000);
            } else {
                showPollTimeout();
            }
        } catch (err) {
            // Network error â€” retry gracefully on next poll
            logger.warn('UPLOAD', 'Poll request failed (network), will retry', { attempt: attempts, jobId, error: err.message, online: navigator.onLine });
            if (attempts < MAX_POLLS) {
                setTimeout(poll, 5000);
            } else {
                showPollTimeout();
            }
        }
    };

    function showPollTimeout() {
        logger.warn('UPLOAD', 'Polling timeout reached (10 min)', { jobId, attempts });
        setStatus(statusEl, 'Processing is taking longer than expected. Your note will appear in the Notes tab when ready.', 'uploading');
        showProgress(progressEl, fillEl, 90);
        if (sessionDbId) {
            try { SessionStore.update(sessionDbId, { status: 'completed', uploadedNoteId: jobId }); } catch (e) {}
            loadRecordingHistory();
        }
        // Show "Check Notes" + "New Recording" buttons
        const container = document.getElementById('newRecordingActions');
        if (container) {
            container.classList.remove('hidden');
            container.innerHTML = `
                <button class="btn" data-action="click-notes-tab">
                    \ud83d\udcdd Check Notes Later
                </button>
                <button class="btn btn-outline" data-action="reset-for-new-recording">
                    \ud83c\udfa4 New Recording
                </button>
            `;
        }
    }

    poll();
}

// ============================================================================
// RECORDING RECOVERY & HISTORY (SessionStore-based)
// ============================================================================

// Migrate legacy RecordingStore entries to SessionStore
